import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import _, { isNil } from 'lodash';
import tw from 'twin.macro';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { Button, Input, Select } from 'antd';
import SubFilterCompareIcon from 'components/molecules/GrowthAnalysis/SubFilterCompareIcon';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import Loader from 'components/molecules/Loader';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { getConfig, parseAttributeType, sendNotification } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_CHANGE_BASELINE_DROPDOWN_CHANGED,
    PRICE_CHANGE_COMPARE_CLICKED,
    PRICE_CHANGE_PRICE_DROPDOWN_CHANGED,
    PRICE_CHANGE_SUB_FILTER_DESELECTED,
    PRICE_CHANGE_SUB_FILTER_SELECTED,
    PRICE_CHANGE_TAB_CHANGED,
} from 'utils/mixpanelEventNames';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import DownSvg from 'assets/svg/Down';
import ExpandedViewPriceChange from 'components/template/PriceChange/ExpandedViewPriceChange';
import CalenderPriceChange from './CalenderPriceChange';
import { useFetchPriceChangeAttributeLevelSales } from 'hooks/priceChange/useFetchPriceChangeAttributeLevelData';
import TileButton from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { frequencyDaysMapping } from 'config/constants';

export const tileSalesDataMapping: any = {
    chartMapping: {
        abs: 0,
        percentage: 1,
    },
    tableMapping: {
        vol: 0,
        val: 1,
        units: 2,
        profit: 3,
    },
};

export const defaultApplyFilter = {
    jsonstring_dim1: {},
    jsonstring_dim2: {},
};

export type salesDataType = {
    chartData: any | null;
    tableData: any | null;
    loading: boolean;
    error: any;
    count: number;
};

export const nullAttrTypeOption = { label: null, value: null, dimension: null }; //change this item's data structure according to attrTypeOptions array item

export const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

const PriceChange = () => {
    const { appConfig } = useContext(UserContext);
    const priceChangeConfig = getConfig('priceChange', appConfig);
    const timePeriodOptions = priceChangeConfig.timePeriodOptions;
    const commonConfig = getConfig('commonConfig', appConfig);
    const {
        salesTileData,
        absTileData,
        volTileData,
        kpiListData,
        dimensionConfig: { excludedDimensions, defaultDimension },
    } = priceChangeConfig;
    const [absTile, setAbsTile] = useState(absTileData[1]);
    const [selectedTimePeriodDropdown, setSelectedTimePeriodDropdown] = useState<any>(timePeriodOptions?.[0]);
    const [selectedKpi, setSelectedKpi] = useState(kpiListData[0]);
    const [volTile, setVolTile] = useState(volTileData[0]);
    const [salesTile, setSalesTile] = useState(salesTileData[0]);
    const [attrTypeOptions, setAttrOptions] = useState({ data: [], loading: false });
    const [subFilterSelectedItems, setSubFilterSelectedItems] = useState([nullAttrTypeOption]);
    const [activeCompareIcons, setActiveCompareIcons] = useState<any[]>([]);
    const [attributeTypeToShow, setAttributeTypesToShow] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [thresholdValue, setThresholdValue] = useState('');
    const [dummyThresholdRender, setDummyThresholdRender] = useState(0);
    const offsetRef = useRef({
        offset: [commonConfig.offset || 0, commonConfig.offset || 0],
        width: [0, 0],
    });

    const baseLinePeriodLabel = 'Compare Period';

    const previousVolTileRef = useRef(volTile);
    const lastItemInHierarchy = useRef<any>({
        dim1: null,
        dim2: null,
    });
    const previousAppliedGlobalFilter = useRef<any>({
        filter: [],
        labels: [],
        previousSelectedItem: [],
    });
    const appliedGlobalFilter = useRef<string>('');
    const applyFilterObj = useRef<any>(null);
    const {
        filterNullError,
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodSelection,
        idForRestOfComponentLoading,
        allCheckedAttribute,
        timeSelectionLabel,
        currentFrequency,
    } = useContext(FilterContextData);

    const [secondStartRange, setSecondStartRange] = useState('');
    const [secondEndRange, setSecondEndRange] = useState('');

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList.loading &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setAttrOptions({ ...attrTypeOptions, data });
                if (subFilterSelectedItems?.length === 1)
                    setSubFilterSelectedItems([
                        data.find((item: any) => item.label === defaultDimension) || data[0],
                        ...subFilterSelectedItems,
                    ]);
            }
        }
        //cleanup
        //reset states to default
        return () => {
            setAttrOptions({ data: [], loading: false });
            setSubFilterSelectedItems([{ ...nullAttrTypeOption }]);
        };
    }, [allDimAndAttributeTypeList?.loading]);

    const stringifiedGlobalFilter = useMemo(() => {
        const stringifiedGlobalFilter = JSON.stringify(idForRestOfComponent);
        //resetting apply filter object
        resetApplyFilterObj(stringifiedGlobalFilter, true);
        return stringifiedGlobalFilter;
    }, [idForRestOfComponent]);

    useEffect(() => {
        if (timePeriodSelection) fetchAllSalesData();
        //update voltile data
        if (!(attributeTypeToShow?.length === 2 && applyFilterObj.current)) {
            previousVolTileRef.current = volTile;
        }
    }, [
        attributeTypeToShow,
        salesTile,
        volTile,
        selectedKpi,
        stringifiedGlobalFilter,
        timePeriodSelection,
        secondStartRange,
        secondEndRange,
        selectedTimePeriodDropdown,
        dummyThresholdRender,
    ]);

    //variables for attribute sales query
    const variables = useMemo(() => {
        //calculate the days difference based on frequency
        const daysDifference = frequencyDaysMapping[currentFrequency?.key ?? 'W'] ?? 0;

        const dayjsStartDate = dayjs(secondStartRange, commonConfig?.dateFormat);
        const dayjsEndDate = dayjs(secondEndRange, commonConfig?.dateFormat);
        const utcOffsetStartDate = dayjs(dayjsStartDate).utcOffset();
        const utcOffsetEndDate = dayjs(dayjsEndDate).utcOffset();

        const startDateUnix = dayjsStartDate
            .add(utcOffsetStartDate, 'm')
            .utc()
            .subtract(daysDifference, 'day')
            .startOf('day')
            .unix();
        const endDateUnix = dayjsEndDate
            .add(utcOffsetEndDate, 'm')
            .utc()
            .subtract(daysDifference, 'day')
            .startOf('day')
            .unix();
        const baselineTimePeriod = {
            start_timestamp: startDateUnix,
            end_timestamp: endDateUnix,
        };

        const tempObj = {
            limit: priceChangeConfig.limit,
            offset: commonConfig.offset,
            frequency: currentFrequency?.key,
            lagList: commonConfig.lagList,
            filters: stringifiedGlobalFilter,
            comparisonPeriodType: selectedTimePeriodDropdown?.value,
            dateRange: [timePeriodSelection, JSON.stringify(baselineTimePeriod)],
            priceChangeThreshold: !thresholdValue ? null : Number(thresholdValue),
        };
        return tempObj;
    }, [
        stringifiedGlobalFilter,
        timePeriodSelection,
        secondEndRange,
        selectedTimePeriodDropdown,
        secondStartRange,
        dummyThresholdRender,
    ]);

    const { loading: applyFilterLoading, applyFilterMutate } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete,
    });

    const {
        data: firstLevelPriceChangeSalesData,
        loading: firstLevelPriceChangeLoading,
        error: firstLevelPriceChangeError,
        count: firstLevelPriceChangeCount,
        comparisonTimePeriod,
        fetchAttributeLevelSales: fetchFirstLevelPriceChangeSales,
    } = useFetchPriceChangeAttributeLevelSales({ salesTile, selectedKpi });

    const {
        data: secondLevelPriceChangeSalesData,
        loading: secondLevelPriceChangeSaleLoading,
        error: secondLevelPriceChangeSaleError,
        count: secondLevelPriceChangeSaleCount,
        fetchAttributeLevelSales: fetchSecondLevelPriceChangeSales,
    } = useFetchPriceChangeAttributeLevelSales({ salesTile, selectedKpi });

    const checkIfNullAttributePresent = (arr: any[]) => {
        let nullExists = false;
        for (let i = 0; i < arr.length; i++) {
            if (!arr[i].value) nullExists = true;
        }
        return nullExists;
    };

    //called when clicked on compare icon
    const compareSubFilterClickHandler = (index: number, active: boolean) => {
        localStorage.setItem('compare_clicked', 'true');
        const tempActiveComparison = [...activeCompareIcons];
        if (!active) {
            tempActiveComparison[0] = index; //only keep 1 active comparison icon at a time
        } else {
            removeComparisonActiveItem(index, tempActiveComparison);
        }
        //resetting apply filter object
        resetApplyFilterObj();
        setSalesTile(salesTileData[0]);
        setActiveCompareIcons(tempActiveComparison);
    };

    const removeComparisonActiveItem = (index: number, arr: any[]) => {
        const idx = arr.indexOf(index);
        if (idx > -1) arr.splice(idx, 1);
    };

    function removePreviousFilter() {
        if (activeCompareIcons.length) {
            resetApplyFilterObj();
        }
        appliedGlobalFilter.current = previousAppliedGlobalFilter.current.filter?.pop();
        previousAppliedGlobalFilter.current.labels?.pop();
        applyFilterObj.current = previousAppliedGlobalFilter.current.previousSelectedItem?.pop();
    }

    //sub filter options change/ reset icon click
    const onSubFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const temp = [...subFilterSelectedItems];
        //if no item and val, it means event is triggered from clear icon
        if (!item && !val) {
            //if clearing last item and drilldown present
            if (index === subFilterSelectedItems.length - 2 && previousAppliedGlobalFilter.current.filter.length) {
                // subtracting 2 to remove null item and considering index here
                //if drilldown present then remove previous drilldown item
                removePreviousFilter();
            } //resetting apply filter object
            else resetApplyFilterObj(filter);
            const removedItem = temp[index];
            //mixpanel event
            trackEventMixpanel(PRICE_CHANGE_SUB_FILTER_DESELECTED, {
                dimension: removedItem?.dimension,
                attributeType: removedItem?.label,
            });
            temp.splice(index, 1); //remove item if cleared
            const tempActiveComparison = [...activeCompareIcons];
            removeComparisonActiveItem(index, tempActiveComparison); //remove comparison icon also on clearing sub filter
            if (index > 0) removeComparisonActiveItem(index - 1, tempActiveComparison); //remove both left and right side comparisons
            if (tempActiveComparison.includes(index + 1))
                tempActiveComparison.splice(tempActiveComparison.indexOf(index + 1), 1, index); //update the compare value
            setActiveCompareIcons(tempActiveComparison);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent && temp.length < attrTypeOptions.data?.length) temp.push(nullAttrTypeOption); //add a new null sub filter if no null sub filter exists and its within the limit
        } else {
            //resetting apply filter object if there is no drilldown present or
            //if drilldown present and changing sub filters except the last subfilter dropdown, reset the apply filter object

            if (
                !previousAppliedGlobalFilter.current.filter.length ||
                (index < subFilterSelectedItems.length - 2 &&
                    !filter &&
                    previousAppliedGlobalFilter.current.filter.length)
            )
                resetApplyFilterObj(filter);

            temp.splice(index, 1, item); //replace item if changed
            if (temp.length < attrTypeOptions.data?.length && temp.length - 1 === index) temp.push(nullAttrTypeOption); //add a new null sub filter if its within the limit

            //mixpanel event
            trackEventMixpanel(PRICE_CHANGE_SUB_FILTER_SELECTED, {
                dimension: item?.dimension,
                attributeType: item?.label,
            });
        }

        setSubFilterSelectedItems(temp);
    };

    //bar chart right click handler
    const barChartRightClickHandler = useCallback(
        (item: any, index: number, applyFilterObject: any) => {
            previousAppliedGlobalFilter.current?.filter?.push(appliedGlobalFilter.current);
            previousAppliedGlobalFilter.current?.labels?.push(applyFilterObject?.attributeExId);
            previousAppliedGlobalFilter.current?.previousSelectedItem?.push(applyFilterObj.current);
            setLoading(true);
            onSubFilterOptionsChange(item.value, item, index, appliedGlobalFilter.current);
            applyFilterObj.current = applyFilterObject;
        },
        [variables, attributeTypeToShow],
    );

    //update the options depending on the selected sub filter
    const memoisedSubFilterDropdownOptions = useMemo(() => {
        const tempOptions: any[] = _.cloneDeep(attrTypeOptions.data);
        subFilterSelectedItems.forEach((item) => {
            const attrTypeIndex = tempOptions.findIndex((elem: any) => elem.label === item?.label);
            if (attrTypeIndex > -1) tempOptions.splice(attrTypeIndex, 1);
        });
        if (tempOptions?.length > 0) {
            lastItemInHierarchy.current.dim1 =
                tempOptions[_.findLastIndex(tempOptions, (item) => item.dimension === 'dim1')] || null;
            lastItemInHierarchy.current.dim2 =
                tempOptions[_.findLastIndex(tempOptions, (item) => item.dimension === 'dim2')] || null;
        } //always store the last item in hierarchy for different dimensions
        return tempOptions;
    }, [subFilterSelectedItems, attrTypeOptions]);

    const arrangeAttrType = (index: number) => {
        return [
            formatAttrTypeItems(subFilterSelectedItems[index]),
            formatAttrTypeItems(subFilterSelectedItems[index + 1]),
        ];
    };

    //attribute sales data
    const salesDataset = useMemo(() => {
        let requiredFirstData: any = { loading: firstLevelPriceChangeLoading || idForRestOfComponentLoading };
        let requiredSecondData: any = { loading: secondLevelPriceChangeSaleLoading || idForRestOfComponentLoading };
        const otherLoadingStates = applyFilterLoading || loading;
        if (otherLoadingStates) {
            //if only 1 attribute type is being shown ,change load of state of 1st attribute type
            if (attributeTypeToShow.length === 1) requiredFirstData.loading = otherLoadingStates;
            //else, change load of state of 2nd attribute type (in comparison mode)
            else {
                requiredSecondData.loading = otherLoadingStates;
            }
        }
        //mapping out the data based on volume tile and absolute tile
        if (
            firstLevelPriceChangeSalesData &&
            'chartData' in firstLevelPriceChangeSalesData &&
            firstLevelPriceChangeSalesData.chartData.length > 0
        ) {
            requiredFirstData.chartData =
                firstLevelPriceChangeSalesData.chartData[tileSalesDataMapping['chartMapping'][`${absTile.key}`]];
            requiredFirstData.tableData = firstLevelPriceChangeSalesData.tableData;
        }
        //mapping out the data based on volume tile and absolute tile
        if (
            secondLevelPriceChangeSalesData &&
            'chartData' in secondLevelPriceChangeSalesData &&
            secondLevelPriceChangeSalesData.chartData.length > 0
        ) {
            requiredSecondData.chartData =
                secondLevelPriceChangeSalesData.chartData[tileSalesDataMapping['chartMapping'][`${absTile.key}`]];
            requiredSecondData.tableData = secondLevelPriceChangeSalesData.tableData;
        }
        const data: salesDataType[] = [
            {
                chartData: requiredFirstData?.chartData || null,
                tableData: requiredFirstData?.tableData || null,
                error: firstLevelPriceChangeError,
                loading: requiredFirstData.loading,
                count: firstLevelPriceChangeCount,
            },
            {
                chartData: requiredSecondData?.chartData || null,
                tableData: requiredSecondData?.tableData || null,
                error: secondLevelPriceChangeSaleError,
                loading: requiredSecondData.loading,
                count: secondLevelPriceChangeSaleCount,
            },
        ];

        return data;
    }, [
        firstLevelPriceChangeError,
        firstLevelPriceChangeLoading,
        firstLevelPriceChangeSalesData,
        secondLevelPriceChangeSaleError,
        secondLevelPriceChangeSaleLoading,
        secondLevelPriceChangeSalesData,
        absTile,
        volTile,
        selectedKpi,
        loading,
        applyFilterLoading,
        firstLevelPriceChangeCount,
        secondLevelPriceChangeSaleCount,
        idForRestOfComponentLoading,
    ]);

    const formatAttrTypeItems = (item: any) => {
        return {
            selectedAttributeType: item.label,
            selectedAttributeValue: item.value,
            selectedAttributeKey: item.key,
            dimension: item.dimension,
            dataset: [],
            disableHover:
                lastItemInHierarchy?.current[item.dimension] === null
                    ? true
                    : lastItemInHierarchy?.current[item.dimension]?.value <= item.value, //disable hover in bar chart for last item
            key: Math.random() + item.label, //adding a random key here so that the item gets re-rendered again
        };
    };

    useEffect(() => {
        resetInfiniteScroller();
        //if comparison available, compare scenarios and show all scenarios user has compared
        if (activeCompareIcons.length > 0) {
            const sortedActiveComparisons = [...activeCompareIcons];
            sortedActiveComparisons.sort((a, b) => a - b); //sort active comparison indices in ascending order
            let tempAttrTypeToShow: any[] = [];

            for (let i = 0; i < sortedActiveComparisons.length; i++) {
                tempAttrTypeToShow.push(...arrangeAttrType(sortedActiveComparisons[i]));
            }
            tempAttrTypeToShow = _.uniqBy(tempAttrTypeToShow, (e) => e.selectedAttributeValue); //remove duplicate sub filter items
            setAttributeTypesToShow(tempAttrTypeToShow);

            if (localStorage.getItem('compare_clicked')) {
                //mixpanel event
                trackEventMixpanel(PRICE_CHANGE_COMPARE_CLICKED, {
                    index: Number(sortedActiveComparisons[0]) + 1,
                    firstAttributeType: tempAttrTypeToShow[0]?.selectedAttributeType,
                    secondAttributeType: tempAttrTypeToShow[1]?.selectedAttributeType,
                    isActive: true,
                });
                localStorage.removeItem('compare_clicked');
            }

            //else if atleast 1 subFilter selected, only show latest sub filter selection
        } else if (subFilterSelectedItems?.length > 1) {
            if (localStorage.getItem('compare_clicked')) {
                //mixpanel event
                trackEventMixpanel(PRICE_CHANGE_COMPARE_CLICKED, {
                    index: null,
                    firstAttributeType: null,
                    secondAttributeType: null,
                    isActive: false,
                });
                localStorage.removeItem('compare_clicked');
            }
            for (let i = subFilterSelectedItems.length - 1; i >= 0; i--) {
                //finding the latest sub filter selection
                if (subFilterSelectedItems[i]?.value !== null)
                    return setAttributeTypesToShow([formatAttrTypeItems(subFilterSelectedItems[i])]);
            }
            //else dont show anything
        } else {
            setAttributeTypesToShow([]);
        }
    }, [activeCompareIcons, subFilterSelectedItems]);

    //util function for calling query
    const fetchSalesData = (variables: any, fn: Function) => {
        fn({ variables });
    };

    //parsing variables for each attribute type selected in subfilter
    function parseVariables(
        attributeType: any,
        filters: null | string = null,
        offset: number | null = null,
        tileKey: string | number | null = null,
    ) {
        const tempObj = {
            ...variables,
            offset: offset === null ? variables.offset : offset,
            filters: filters ? filters : variables.filters,
            attributeType: attributeType.selectedAttributeKey,
            dimension: attributeType.dimension,
            fromTab: tileKey ? tileKey : salesTile.key,
            metricType: volTile.dataKey,
            priceType: selectedKpi.value,
            volTile: volTile,
        };
        return tempObj;
    }

    //util function to update variables and execute query
    function updateVariablesAndExecuteQuery(
        item: any,
        fn: Function,
        filters: null | string = null,
        offset: number | null = null,
        tileKey: string | number | null = null,
    ) {
        const updatedVariables = parseVariables(item, filters, offset, tileKey);
        fetchSalesData(updatedVariables, fn);
    }

    //wrapper function to fetch attribute sales data
    const fetchAllSalesData = useCallback(() => {
        //resetting to default
        resetInfiniteScroller();
        if (applyFilterObj.current) {
            //apply filter call
            prepareApplyFilterVariableAndExecute();
        } else {
            // call attribute sales api once
            if (attributeTypeToShow.length === 1) {
                updateVariablesAndExecuteQuery(attributeTypeToShow[0], fetchFirstLevelPriceChangeSales, undefined, 0);
                // call attribute sales api for different attribute types (max 2)
            } else if (attributeTypeToShow.length === 2) {
                updateVariablesAndExecuteQuery(attributeTypeToShow[0], fetchFirstLevelPriceChangeSales, undefined, 0);
                updateVariablesAndExecuteQuery(attributeTypeToShow[1], fetchSecondLevelPriceChangeSales, undefined, 0);
            }
        }
    }, [attributeTypeToShow, fetchFirstLevelPriceChangeSales, fetchSecondLevelPriceChangeSales, variables]);

    //called when infinite scroll happens with apply filter
    const infiniteScrollerApplyFilterOnNext = (filter: string, index: number) => {
        if (index === 0) {
            return updateVariablesAndExecuteQuery(
                attributeTypeToShow[0],
                fetchFirstLevelPriceChangeSales,
                filter,
                offsetRef.current.offset[index],
            );
        } else {
            {
                return updateVariablesAndExecuteQuery(
                    attributeTypeToShow[1],
                    fetchSecondLevelPriceChangeSales,
                    filter,
                    offsetRef.current.offset[index],
                );
            }
        }
    };

    //reset infinite scroller ref variable
    function resetInfiniteScroller() {
        offsetRef.current.offset = [commonConfig.offset || 0, commonConfig.offset || 0];
        offsetRef.current.width = [0, 0];
    }

    function resetApplyFilterObj(filter?: string, clearPreviousFilter: boolean = false) {
        applyFilterObj.current = null;
        appliedGlobalFilter.current = filter ? filter : stringifiedGlobalFilter;
        if (!filter || clearPreviousFilter)
            previousAppliedGlobalFilter.current = {
                filter: [],
                labels: [],
                previousSelectedItem: [],
            };
    }

    //called when user infinite scrolls
    const infiniteScrollerOnNext = useCallback(
        (index: number, width: number) => {
            offsetRef.current.width[index] = width;
            const newOffset = Math.max(0, (salesDataset[index]?.chartData?.data?.length ?? 0) - 1);
            if (newOffset >= (salesDataset[index]?.count ?? 0)) return;
            offsetRef.current.offset[index] = newOffset;
            if (index === 0) {
                return updateVariablesAndExecuteQuery(
                    attributeTypeToShow[0],
                    fetchFirstLevelPriceChangeSales,
                    attributeTypeToShow.length === 1 ? appliedGlobalFilter.current : undefined, //if attributestoshow is 2 it means 1st attribute should always use default global filter selection
                    newOffset,
                    salesTile.key,
                );
            }

            if (index === 1)
                return updateVariablesAndExecuteQuery(
                    attributeTypeToShow[1],
                    fetchSecondLevelPriceChangeSales,
                    appliedGlobalFilter.current,
                    newOffset,
                    salesTile.key,
                );
        },
        [attributeTypeToShow, salesTile, variables, salesDataset],
    );

    //called when clicked on item drill down in bar chart
    const drillDownHandler = useCallback(
        (item: any, index: number, applyFilterObject: any) => {
            //if equal then undo action
            //this will only happen in comparison mode
            if (_.isEqual(applyFilterObj.current, applyFilterObject)) {
                //resetting apply filter object
                resetApplyFilterObj();
                resetInfiniteScroller();
                updateVariablesAndExecuteQuery(attributeTypeToShow[1], fetchSecondLevelPriceChangeSales);
                return;
            }
            setLoading(true);
            const tempAttributeIndex = memoisedSubFilterDropdownOptions.findIndex(
                (option: any) => option.value > item.value,
            );
            if (
                tempAttributeIndex > -1 &&
                checkIfNullAttributePresent(subFilterSelectedItems) &&
                attributeTypeToShow.length === 1
            ) {
                previousAppliedGlobalFilter.current?.filter?.push(appliedGlobalFilter.current);
                previousAppliedGlobalFilter.current?.labels?.push(applyFilterObject?.attributeExId);
                previousAppliedGlobalFilter.current?.previousSelectedItem?.push(applyFilterObj.current);
                onSubFilterOptionsChange(
                    memoisedSubFilterDropdownOptions[tempAttributeIndex].value,
                    memoisedSubFilterDropdownOptions[tempAttributeIndex],
                    index,
                    appliedGlobalFilter.current,
                );
                applyFilterObj.current = applyFilterObject;
            } else {
                //when 2 attributetypes to show always reset applied  filter to global filter
                resetApplyFilterObj();
                applyFilterObj.current = applyFilterObject;
                previousAppliedGlobalFilter.current?.filter?.push(appliedGlobalFilter.current);
                previousAppliedGlobalFilter.current?.labels?.push(applyFilterObject?.attributeExId);
                previousAppliedGlobalFilter.current?.previousSelectedItem?.push(applyFilterObj.current);
                fetchAllSalesData();
            }
        },
        [
            memoisedSubFilterDropdownOptions,
            subFilterSelectedItems,
            attributeTypeToShow,
            variables,
            volTile,
            selectedKpi,
        ],
    );

    //apply filter on complete
    function onApplyFilterComplete(payload: any) {
        // const tempApplyFilterResponse = _.cloneDeep(idForRestOfComponent);
        const tempGlobalFilter = appliedGlobalFilter.current
            ? JSON.parse(appliedGlobalFilter.current)
            : idForRestOfComponent;
        const tempApplyFilterResponse = _.cloneDeep(tempGlobalFilter);
        let commonIdsDim1 = [];
        let commonIdsDim2 = [];
        if (payload?.dim1?.ids?.length > 0) {
            commonIdsDim1 = [...payload.dim1.ids];
            if (tempApplyFilterResponse.dim1.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim1 = _.intersection(tempApplyFilterResponse.dim1.ids, payload.dim1.ids);
            }
            tempApplyFilterResponse.dim1.ids = [...commonIdsDim1];
        }
        if (payload?.dim2?.ids?.length > 0) {
            commonIdsDim2 = [...payload.dim2.ids];
            if (tempApplyFilterResponse.dim2.ids?.length > 0) {
                //if no id in global filter , it means all are selected
                commonIdsDim2 = _.intersection(tempApplyFilterResponse.dim2.ids, payload.dim2.ids);
            }
            tempApplyFilterResponse.dim2.ids = [...commonIdsDim2];
        }
        const stringifiedApplyFilterResponse = JSON.stringify(tempApplyFilterResponse);

        appliedGlobalFilter.current = stringifiedApplyFilterResponse;
        setLoading(false);
        //if call coming from infinite scroll, redirect to infinite scroll function
        if (offsetRef.current.offset[0] !== commonConfig.offset) {
            return infiniteScrollerApplyFilterOnNext(stringifiedApplyFilterResponse, 0);
        } else if (offsetRef.current.offset[1] !== commonConfig.offset) {
            return infiniteScrollerApplyFilterOnNext(stringifiedApplyFilterResponse, 1);
        }
        //if length is 1 , comparison is not present and hence call only for 1st attribute type
        if (attributeTypeToShow.length === 1)
            updateVariablesAndExecuteQuery(
                attributeTypeToShow[0],
                fetchFirstLevelPriceChangeSales,
                stringifiedApplyFilterResponse,
            );
        //if length is 2 , comparison is present and hence call only for 2nd attribute type if call coming from same voltile
        else {
            //checking if call coming from same voltile
            if (previousVolTileRef.current?.key !== volTile.key) {
                updateVariablesAndExecuteQuery(attributeTypeToShow[0], fetchFirstLevelPriceChangeSales, undefined);
            }
            updateVariablesAndExecuteQuery(
                attributeTypeToShow[1],
                fetchSecondLevelPriceChangeSales,
                stringifiedApplyFilterResponse,
            );
        }
        previousVolTileRef.current = volTile;
    }
    //apply filter call
    function prepareApplyFilterVariableAndExecute() {
        const tempApplyFilterVairable: any = _.cloneDeep(defaultApplyFilter);
        if (applyFilterObj.current.dimension === 'dim1') {
            tempApplyFilterVairable.jsonstring_dim1.inclusion = [
                {
                    ...applyFilterObj.current,
                },
            ];
        } else {
            tempApplyFilterVairable.jsonstring_dim2.inclusion = [
                {
                    ...applyFilterObj.current,
                },
            ];
        }
        tempApplyFilterVairable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVairable.jsonstring_dim1);
        tempApplyFilterVairable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVairable.jsonstring_dim2);
        applyFilterMutate({ variables: tempApplyFilterVairable });
    }

    const renderBaseLinePeriodLabel = () => {
        if (selectedTimePeriodDropdown?.value === 'custom' || !comparisonTimePeriod || firstLevelPriceChangeLoading)
            return null;

        const daysDifference = frequencyDaysMapping[currentFrequency?.key ?? 'W'] ?? 0;

        const startDate = dayjs
            .unix(comparisonTimePeriod?.startTimestamp)
            .add(daysDifference, 'day')
            .utc()
            .format(commonConfig.dateFormat);
        const endDate = dayjs
            .unix(comparisonTimePeriod?.endTimestamp)
            .add(daysDifference, 'day')
            .utc()
            .format(commonConfig.dateFormat);

        return (
            <div className="w-[180px]">
                <CalenderPriceChange
                    position="bottom"
                    setStartDate={() => {}}
                    setEndDate={() => {}}
                    startDate={startDate}
                    endDate={endDate}
                    calenderHeader={''}
                    isDisabled
                />
            </div>
        );
    };

    const additionalDataCopy = useMemo(() => {
        if (selectedTimePeriodDropdown?.value === 'custom' || !comparisonTimePeriod || firstLevelPriceChangeLoading)
            return { ['Comparison Period']: [`${secondStartRange} - ${secondEndRange}`] };
        //calculate the days difference based on frequency
        const daysDifference = frequencyDaysMapping[currentFrequency?.key ?? 'W'] ?? 0;
        const startDate = dayjs
            .unix(comparisonTimePeriod?.startTimestamp)
            .add(daysDifference, 'day')
            .utc()
            .format(commonConfig.dateFormat);
        const endDate = dayjs
            .unix(comparisonTimePeriod?.endTimestamp)
            .add(daysDifference, 'day')
            .utc()
            .format(commonConfig.dateFormat);
        return { ['Comparison Period']: [`${startDate} - ${endDate}`] };
    }, [
        secondEndRange,
        secondStartRange,
        selectedTimePeriodDropdown,
        comparisonTimePeriod,
        firstLevelPriceChangeCount,
    ]);

    return (
        <>
            {/* SUB FILTER CONTAINER */}
            <div className="flex gap-2 mb-4 overflow-x-auto">
                {subFilterSelectedItems.map((item, index) => {
                    return (
                        <div key={index} className="flex gap-2 items-center">
                            <SubFilterDropdown
                                loading={
                                    allDimAndAttributeTypeList?.loading ||
                                    salesDataset[0].loading ||
                                    salesDataset[1].loading ||
                                    false
                                }
                                onChange={onSubFilterOptionsChange}
                                key={index}
                                index={index}
                                options={memoisedSubFilterDropdownOptions}
                                value={item.label}
                                from="growth-analysis"
                            />
                            {subFilterSelectedItems?.length - 1 !== index && (
                                <SubFilterCompareIcon
                                    disabled={
                                        subFilterSelectedItems[index + 1]?.label === null ||
                                        salesDataset[0].loading ||
                                        salesDataset[1].loading
                                    }
                                    onClick={compareSubFilterClickHandler}
                                    index={index}
                                    active={activeCompareIcons.includes(index)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* GROWTH ANALYSIS CONTAINER */}
            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg
            // {allDimAndAttributeTypeList?.loading ? 'h-[calc(100vh-155px)]' : ''}
            
            `}
            >
                {filterNullError ? (
                    <NoDataForSelection />
                ) : (
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center">
                                <h2 className="font-medium font-xl m-0 text-headerColor">Price Change</h2>
                            </div>
                        </div>
                        <div className="flex justify-between mb-4">
                            <div className="flex items-end gap-4">
                                <div>
                                    <p className="text-greyFont font-medium pb-1 font-xs">{baseLinePeriodLabel}</p>
                                    <CustomSelect
                                        className="dropdown-height dropdown-width-multi-select uniqSelPCBaseLinePeriodDropdown"
                                        suffixIcon={<DownSvg />}
                                        value={selectedTimePeriodDropdown?.value}
                                        disabled={false}
                                        options={timePeriodOptions}
                                        onChange={(val: any, item: any) => {
                                            setSelectedTimePeriodDropdown(item);

                                            //mixpanel event
                                            trackEventMixpanel(PRICE_CHANGE_BASELINE_DROPDOWN_CHANGED, {
                                                periodName: item?.label,
                                            });
                                        }}
                                    />
                                </div>
                                {selectedTimePeriodDropdown?.value === 'custom' ? (
                                    <div className="w-[180px]">
                                        <CalenderPriceChange
                                            position="bottom"
                                            setStartDate={setSecondStartRange}
                                            setEndDate={setSecondEndRange}
                                            startDate={secondStartRange}
                                            endDate={secondEndRange}
                                            calenderHeader=""
                                        />
                                    </div>
                                ) : (
                                    <>{renderBaseLinePeriodLabel()}</>
                                )}
                            </div>
                            <div className="flex gap-4 items-end">
                                <div>
                                    <p className="text-greyFont font-medium pb-1 font-xs">Max % Price Chg Threshold</p>
                                    <div className="flex items-center gap-1">
                                        <Input
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' &&
                                                    !(firstLevelPriceChangeLoading || secondLevelPriceChangeSaleLoading)
                                                ) {
                                                    setDummyThresholdRender(dummyThresholdRender + 1);
                                                }
                                            }}
                                            type={'number'}
                                            min={0}
                                            value={thresholdValue}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '' || Number(val) >= 0) {
                                                    setThresholdValue(val);
                                                } else if (Number(val) < 0) {
                                                    sendNotification('error', 'Threshold cannot be negative');
                                                }
                                            }}
                                            placeholder="Enter threshold"
                                            style={{ width: 150, borderRadius: 6 }}
                                        />
                                        <Button
                                            disabled={firstLevelPriceChangeLoading || secondLevelPriceChangeSaleLoading}
                                            type="primary"
                                            style={{ borderRadius: 6, fontSize: 13 }}
                                            onClick={() => {
                                                setDummyThresholdRender(dummyThresholdRender + 1);
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </div>

                                <CustomSelect
                                    className="dropdown-height dropdown-width uniqSelPCPriceTypeDropdown"
                                    suffixIcon={<DownSvg />}
                                    value={selectedKpi?.value}
                                    disabled={allDimAndAttributeTypeList?.loading}
                                    options={kpiListData}
                                    onChange={(val: any, item: any) => {
                                        setSelectedKpi(item);

                                        //mixpanel event
                                        trackEventMixpanel(PRICE_CHANGE_PRICE_DROPDOWN_CHANGED, {
                                            kpiName: item?.label,
                                        });
                                    }}
                                />
                                <TileButton
                                    disabled={allDimAndAttributeTypeList?.loading}
                                    onClick={(item: any) => {
                                        setVolTile(item);

                                        //mixpanel event
                                        trackEventMixpanel(PRICE_CHANGE_TAB_CHANGED, {
                                            tabName: item?.label,
                                        });
                                    }}
                                    activeKey={volTile.key}
                                    data={priceChangeConfig.volTileData}
                                />
                            </div>
                        </div>

                        {allDimAndAttributeTypeList?.loading ? (
                            <div
                                style={{ height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            >
                                <Loader />
                            </div>
                        ) : selectedTimePeriodDropdown?.value === 'custom' &&
                          (secondStartRange === '' || secondEndRange === '') ? (
                            <div>
                                <ErrorBox msg="enterDateRange" />
                            </div>
                        ) : (
                            attributeTypeToShow.map((item, index) => {
                                return (
                                    <div key={item.key} className="flex ">
                                        {/* MAKE SURE TO MEMOIZE FUNCTIONS AND VARIABLES */}
                                        <ExpandedViewPriceChange
                                            selectedTimePeriodDropdown={selectedTimePeriodDropdown}
                                            stringifiedFilter={appliedGlobalFilter.current} //not
                                            previousApplyFilter={previousAppliedGlobalFilter.current} //not
                                            drillDownSelectedData={applyFilterObj.current} //yes
                                            attributeTypeItems={attributeTypeToShow} //yes
                                            attributeTypeItem={item} //yes
                                            index={index} //yes
                                            absTile={absTile} //not req
                                            volTile={volTile} //not req
                                            salesTileData={salesTileData}
                                            salesTile={salesTile}
                                            drillDownHandler={drillDownHandler}
                                            dropdownItems={memoisedSubFilterDropdownOptions}
                                            dropdownClickHandler={barChartRightClickHandler}
                                            attrTypeOptions={attrTypeOptions}
                                            salesDataset={salesDataset}
                                            infiniteScrollerOnNext={infiniteScrollerOnNext}
                                            infiniteScrollSettings={offsetRef.current}
                                            secondStartRange={secondStartRange}
                                            secondEndRange={secondEndRange}
                                            selectedKpi={selectedKpi}
                                            timePeriodSelection={JSON.parse(timePeriodSelection)}
                                            dateFormat={commonConfig.dateFormat}
                                            timeSelectionLabel={timeSelectionLabel}
                                            additionalDataCopy={additionalDataCopy}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default PriceChange;
